import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, API_BASE_URL } from '../services/api';
import ContentViewer from '../components/ContentViewer';
import { 
  Loader2, ArrowLeft, Sparkles, CheckCircle, Circle, 
  AlertTriangle, Code, FileText, BookOpen, Play, X
} from 'lucide-react';

interface GenerationJob {
  job_id: number;
  chapter_id: number;
  status: 'created' | 'planning' | 'generating_content' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  current_step: string;
  current_section?: string;
  outline_plan?: {
    chapter_title: string;
    sections: Array<{
      section_id: string;
      section_title: string;
      content_points: string[];
      code_examples: string[];
      visualizations: string[];
    }>;
  };
  generated_sections: Array<{
    section_id: string;
    title: string;
    preview: string;
  }>;
  final_content?: string;
  draft_id?: number;
  error?: string;
}

const normalizeJob = (data: Partial<GenerationJob>): GenerationJob => ({
  job_id: data.job_id || 0,
  chapter_id: data.chapter_id || 0,
  status: data.status || 'created',
  progress: data.progress ?? 0,
  current_step: data.current_step || 'Preparing generation...',
  current_section: data.current_section,
  outline_plan: data.outline_plan,
  generated_sections: data.generated_sections || [],
  final_content: data.final_content,
  draft_id: data.draft_id,
  error: data.error,
});

export default function GenerateChapter() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  
  const [step, setStep] = useState<'input' | 'planning' | 'generating' | 'review'>('input');
  const [chapterNumber, setChapterNumber] = useState(1);
  const [existingChapters, setExistingChapters] = useState<number[]>([]);
  const [chapterTitle, setChapterTitle] = useState('');
  const [outline, setOutline] = useState('');
  const [job, setJob] = useState<GenerationJob | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const pollInterval = useRef<NodeJS.Timeout | null>(null);

  // Get next available chapter number on load
  const [chapterList, setChapterList] = useState<Array<{number: number, title: string}>>([]);
  
  useEffect(() => {
    const loadChapters = async () => {
      try {
        const chapters = await api.getChapters(parseInt(courseId!));
        const sortedChapters = chapters.sort((a, b) => a.chapter_number - b.chapter_number);
        const existingNums = sortedChapters.map(c => c.chapter_number);
        const chapterInfo = sortedChapters.map(c => ({number: c.chapter_number, title: c.title}));
        setExistingChapters(existingNums);
        setChapterList(chapterInfo);
        const maxChapter = existingNums.length > 0 ? Math.max(...existingNums) : 0;
        setChapterNumber(maxChapter + 1);
      } catch (e) {
        console.error('Failed to load chapters:', e);
      }
    };
    loadChapters();
  }, [courseId]);

  // Poll for job status
  useEffect(() => {
    if (job?.job_id && (job.status === 'planning' || job.status === 'generating_content')) {
      pollInterval.current = setInterval(async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/teacher/generator/jobs/${job.job_id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });
          if (response.ok) {
            const data = normalizeJob(await response.json());
            setJob(data);
            
            if (data.status === 'completed') {
              setStep('review');
              if (pollInterval.current) clearInterval(pollInterval.current);
            } else if (data.status === 'failed') {
              setError(data.error || 'Generation failed');
              if (pollInterval.current) clearInterval(pollInterval.current);
            }
          }
        } catch (e) {
          console.error('Poll error:', e);
        }
      }, 2000);
    }
    
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, [job?.job_id, job?.status]);

  const handleCreateJob = async () => {
    if (!chapterTitle.trim() || !outline.trim()) {
      setError('Please provide chapter title and outline');
      return;
    }
    
    if (existingChapters.includes(chapterNumber)) {
      setError(`Chapter ${chapterNumber} already exists. Please choose a different number.`);
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/teacher/generator/jobs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          course_id: parseInt(courseId!, 10),
          chapter_number: chapterNumber,
          title: chapterTitle,
          outline,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to create job: ${response.status}`);
      }
      
      const data = normalizeJob(await response.json());
      setJob(data);
      setStep('planning');
      
      // Start generation
      const startResponse = await fetch(`${API_BASE_URL}/teacher/generator/jobs/${data.job_id}/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (!startResponse.ok) throw new Error('Failed to start generation');
      setJob(prev => prev ? {
        ...prev,
        status: 'planning',
        current_step: 'Creating outline plan...',
        progress: Math.max(prev.progress || 0, 5),
        generated_sections: prev.generated_sections || [],
      } : prev);
      
    } catch (err: any) {
      setError(err.message || 'Failed to create generation job');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToEditor = () => {
    navigate(`/teacher/edit/${courseId}`);
  };

  const handleCancel = async () => {
    if (!job?.job_id) return;
    
    try {
      await fetch(`${API_BASE_URL}/teacher/generator/jobs/${job.job_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
    } catch (e) {
      // Ignore
    }
    
    navigate(`/teacher/edit/${courseId}`);
  };

  // Sample outline
  const sampleOutline = `第 3 章：机器学习基础

3.1 什么是机器学习
3.2 监督学习 vs 非监督学习
3.3 训练集、验证集与测试集
3.4 模型评估指标
3.5 过拟合与欠拟合

【案例研究】：房价预测模型实战`;

  const generatedSections = job?.generated_sections || [];
  const outlineSections = job?.outline_plan?.sections || [];
  const progress = Math.min(Math.max(job?.progress ?? 0, 0), 100);

  return (
    <div className="min-h-screen bg-apple-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-apple-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/teacher/edit/${courseId}`)}
              className="flex items-center gap-2 text-apple-text-secondary hover:text-apple-text"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Editor
            </button>
            <div className="w-px h-6 bg-apple-border" />
            <h1 className="text-lg font-semibold text-apple-text flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-apple-purple" />
              AI Chapter Generator
            </h1>
          </div>
          
          {job && (
            <div className="flex items-center gap-3">
              <span className={`text-sm px-3 py-1 rounded-full ${
                job.status === 'completed' ? 'bg-green-100 text-green-700' :
                job.status === 'failed' ? 'bg-red-100 text-red-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {job.status}
              </span>
              {job.status !== 'completed' && job.status !== 'failed' && (
                <button onClick={handleCancel} className="text-apple-text-secondary hover:text-red-600">
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-apple-sm text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            {error}
          </div>
        )}
        {/* Step 1: Input */}
        {step === 'input' && (
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-apple-lg shadow-sm border border-apple-border p-8">
              <h2 className="text-xl font-semibold text-apple-text mb-6">Create New Chapter</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-apple-text mb-2">
                    Chapter Number
                  </label>
                  <input
                    type="number"
                    value={chapterNumber}
                    onChange={(e) => setChapterNumber(parseInt(e.target.value))}
                    className={`input w-32 ${existingChapters.includes(chapterNumber) ? 'border-red-500 bg-red-50' : ''}`}
                    min={1}
                  />
                  {existingChapters.includes(chapterNumber) && (
                    <p className="text-sm text-red-600 mt-1">
                      ⚠️ Chapter {chapterNumber} already exists! 
                      Next available: {Math.max(...existingChapters, 0) + 1}
                    </p>
                  )}
                  {chapterList.length > 0 && (
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium text-apple-text mb-2">Existing chapters in this course:</p>
                      <ul className="text-xs text-apple-text-secondary space-y-1 max-h-32 overflow-y-auto">
                        {chapterList.map(ch => (
                          <li key={ch.number} className={ch.number === chapterNumber ? 'text-red-600 font-medium' : ''}>
                            Ch {ch.number}: {ch.title} {ch.number === chapterNumber ? '⚠️ EXISTS' : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-apple-text mb-2">
                    Chapter Title
                  </label>
                  <input
                    type="text"
                    value={chapterTitle}
                    onChange={(e) => setChapterTitle(e.target.value)}
                    className="input w-full"
                    placeholder="e.g., Machine Learning Fundamentals"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-apple-text mb-2">
                    Chapter Outline
                  </label>
                  <p className="text-sm text-apple-text-secondary mb-2">
                    Provide a structured outline with sections and subsections.
                  </p>
                  <textarea
                    value={outline}
                    onChange={(e) => setOutline(e.target.value)}
                    className="input w-full h-64 font-mono text-sm"
                    placeholder={sampleOutline}
                  />
                  <button
                    onClick={() => setOutline(sampleOutline)}
                    className="text-sm text-apple-blue hover:underline mt-2"
                  >
                    Load sample outline
                  </button>
                </div>

                <div className="flex items-center gap-4 pt-4">
                  <button
                    onClick={handleCreateJob}
                    disabled={isLoading}
                    className="btn-primary flex items-center gap-2"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                    Start Generation
                  </button>
                  <button
                    onClick={() => navigate(`/teacher/edit/${courseId}`)}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2 & 3: Planning & Generating */}
        {(step === 'planning' || step === 'generating') && job && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Progress */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-apple-lg shadow-sm border border-apple-border p-6">
                <h3 className="font-semibold text-apple-text mb-4">Generation Progress</h3>
                
                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-apple-text-secondary">{job.current_step || 'Preparing generation...'}</span>
                    <span className="font-medium text-apple-text">{progress}%</span>
                  </div>
                  <div className="w-full h-3 bg-apple-gray rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-apple-blue to-apple-purple transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>

                {/* Steps */}
                <div className="space-y-3">
                  <div className={`flex items-center gap-3 ${progress >= 20 ? 'text-green-600' : 'text-apple-text-secondary'}`}>
                    {progress >= 20 ? <CheckCircle className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                    <span className="text-sm">Outline Planning</span>
                  </div>
                  <div className={`flex items-center gap-3 ${progress >= 50 ? 'text-green-600' : 'text-apple-text-secondary'}`}>
                    {progress >= 50 ? <CheckCircle className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                    <span className="text-sm">Content Generation</span>
                  </div>
                  <div className={`flex items-center gap-3 ${progress >= 95 ? 'text-green-600' : 'text-apple-text-secondary'}`}>
                    {progress >= 95 ? <CheckCircle className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                    <span className="text-sm">Finalizing</span>
                  </div>
                </div>
              </div>

              {/* Current Section */}
              {job.current_section && (
                <div className="bg-white rounded-apple-lg shadow-sm border border-apple-border p-6">
                  <h3 className="font-semibold text-apple-text mb-2">Current Section</h3>
                  <div className="flex items-center gap-2 text-apple-text-secondary">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">{job.current_section}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Right: Content Preview */}
            <div className="lg:col-span-2 space-y-6">
              {/* Outline Plan */}
              {job.outline_plan && (
                <div className="bg-white rounded-apple-lg shadow-sm border border-apple-border p-6">
                  <h3 className="font-semibold text-apple-text mb-4 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-apple-blue" />
                    AI Generated Outline Plan
                  </h3>
                  <div className="space-y-4">
                    {outlineSections.length > 0 ? outlineSections.map((section, idx) => (
                      <div key={idx} className="border-l-4 border-apple-blue pl-4 py-2">
                        <h4 className="font-medium text-apple-text">
                          {section.section_id || `${chapterNumber}.${idx + 1}`} {section.section_title || `Section ${idx + 1}`}
                        </h4>
                        {section.content_points?.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {section.content_points.slice(0, 3).map((point, pidx) => (
                              <li key={pidx} className="text-sm text-apple-text-secondary flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-apple-blue rounded-full" />
                                {point}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )) : (
                      <div className="bg-apple-bg rounded-lg p-4 text-sm text-apple-text-secondary">
                        The outline plan will appear here as soon as the AI finishes planning.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Generated Sections */}
              {generatedSections.length === 0 && (
                <div className="bg-white rounded-apple-lg shadow-sm border border-apple-border p-6">
                  <h3 className="font-semibold text-apple-text mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-apple-purple" />
                    Generated Sections
                  </h3>
                  <div className="rounded-lg border border-dashed border-apple-border bg-apple-bg p-6 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-apple-blue mx-auto mb-3" />
                    <p className="text-sm font-medium text-apple-text">Waiting for section content</p>
                    <p className="text-xs text-apple-text-secondary mt-1">
                      Completed sections will appear here while the AI writes the chapter.
                    </p>
                  </div>
                </div>
              )}

              {generatedSections.length > 0 && (
                <div className="bg-white rounded-apple-lg shadow-sm border border-apple-border p-6">
                  <h3 className="font-semibold text-apple-text mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-apple-purple" />
                    Generated Sections ({generatedSections.length})
                  </h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {generatedSections.map((section, idx) => (
                      <div key={idx} className="bg-apple-bg rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm text-apple-text">{section.title || `Section ${idx + 1}`}</span>
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        </div>
                        <div className="text-xs text-apple-text-secondary font-mono line-clamp-3">
                          {section.preview || 'Preview will appear here.'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Code Preview */}
              <div className="bg-slate-900 rounded-apple-lg p-6 text-slate-50">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Code className="w-5 h-5" />
                  Live Code Preview
                </h3>
                <div className="font-mono text-sm space-y-2">
                  <div className="text-slate-400">&lt;!-- {job.current_step} --&gt;</div>
                  {job.current_section && (
                    <div className="text-green-400">
                      &lt;section id="..."&gt;<br/>
                      &nbsp;&nbsp;&lt;h2&gt;{job.current_section}&lt;/h2&gt;<br/>
                      &nbsp;&nbsp;&lt;p&gt;Generating content...&lt;/p&gt;<br/>
                      &lt;/section&gt;
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {step === 'review' && job && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-apple-lg shadow-sm border border-apple-border p-8">
              <div className="text-center mb-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-semibold text-apple-text">Generation Complete!</h2>
                <p className="text-apple-text-secondary mt-2">
                  Your chapter has been generated with {generatedSections.length} sections
                </p>
              </div>

              {/* Preview */}
              <div className="mb-8">
                <h3 className="font-semibold text-apple-text mb-4">Content Preview</h3>
                <div className="bg-apple-bg rounded-lg p-6 max-h-[70vh] overflow-y-auto">
                  <ContentViewer content={job.final_content || ''} />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={handleBackToEditor}
                  className="btn-primary flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Editor
                </button>
                <button
                  onClick={() => setStep('input')}
                  className="btn-outline"
                >
                  Generate Another
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
